import ReactSelect, {
  type SingleValue,
  type MultiValue,
  type StylesConfig,
  type CSSObjectWithLabel,
  type GroupBase,
  type OptionProps,
} from 'react-select'

import {colorCodes} from 'definitions/constant'

export interface SelectOptionType {
  value: string
  label: string
}

export interface SelectProps {
  options: SelectOptionType[]
  value: SelectOptionType | null
  onChange: (value: SelectOptionType) => void
  placeholder?: string
  customStyles?: StylesConfig<SelectOptionType>
  ariaLabel?: string
  isSearchable?: boolean
  id?: string
  isDisabled?: boolean
}

const primaryStyling: StylesConfig<SelectOptionType> = {
  control: (provided: CSSObjectWithLabel, {isDisabled}) => {
    return {
      ...provided,
      backgroundColor: colorCodes.dopeBlack,
      cursor: 'pointer',
      borderRadius: '0px',
      border: '0.5px solid white',
      boxShadow: 'none',
      ':hover': {
        border: '0.5px solid white',
      },
      ...(isDisabled && {
        opacity: 0.3,
      }),
    }
  },
  singleValue: (provided: CSSObjectWithLabel) => ({
    ...provided,
    color: colorCodes.white,
    fontSize: '13px',
  }),
  menu: (provided: CSSObjectWithLabel) => ({
    ...provided,
    backgroundColor: colorCodes.dopeBlack,
  }),
  option: (
    provided: CSSObjectWithLabel,
    {
      isFocused,
      isSelected,
    }: OptionProps<SelectOptionType, boolean, GroupBase<SelectOptionType>>,
  ) => ({
    ...provided,
    color: colorCodes.white,
    backgroundColor:
      isSelected || isFocused ? colorCodes.gray1100 : colorCodes.dopeBlack,
    cursor: 'pointer',
    fontSize: '14px',
    ':hover': {
      backgroundColor: colorCodes.gray1100,
    },
  }),
  placeholder: (provided: CSSObjectWithLabel) => ({
    ...provided,
    color: colorCodes.gray1000,
    fontSize: '14px',
  }),
  input: (provided: CSSObjectWithLabel) => ({
    ...provided,
    color: colorCodes.white,
    fontSize: '14px',
  }),
}

export const Select: React.FC<SelectProps> = ({
  id,
  options,
  value,
  onChange,
  placeholder = 'Select an option...',
  customStyles,
  ariaLabel,
  isSearchable = false,
  isDisabled = false,
}) => {
  // Handle the onChange to adapt to single or multi-select based on isMulti.
  const handleChange = (
    selected: SingleValue<SelectOptionType> | MultiValue<SelectOptionType>,
  ): void => {
    onChange(selected as SelectOptionType)
  }

  return (
    <ReactSelect
      id={id}
      aria-label={ariaLabel}
      value={value}
      placeholder={placeholder}
      options={options}
      onChange={handleChange}
      styles={{...primaryStyling, ...customStyles}}
      isSearchable={isSearchable}
      components={{IndicatorSeparator: () => null}}
      isDisabled={isDisabled}
    />
  )
}
